import { Injectable, OnDestroy } from "@angular/core";
import { PaymentStatusState, PaymentStatusUpdate } from "@models/payment";
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  Subject,
  Subscription,
} from "rxjs";
import { WebSocketService } from "./websocket.service";

const SELLER_PAYMENT_STATUS_CHANNEL = "SellerPaymentStatusChannel";

@Injectable({
  providedIn: "root",
})
export class PaymentNotificationService implements OnDestroy {
  private paymentStatusSubject$ = new Subject<PaymentStatusUpdate>();
  private currentState: PaymentStatusState = {
    orderId: null,
    status: null,
    loading: false,
    error: null,
    data: null,
  };
  private stateSubject$ = new BehaviorSubject<PaymentStatusState>(
    this.currentState,
  );
  private wsSubscription: Subscription | null = null;
  private wsConnectionSubscription: Subscription | null = null;
  private currentOrderId: number | null = null;
  private currentSellerPaymentRequestId: number | null = null;
  private unsubscribeTimeout: any = null;

  constructor(private webSocketService: WebSocketService) {}

  ngOnDestroy(): void {
    this.cleanupAll();
  }

  /**
   * Inicia o monitoramento de um pedido específico
   */
  startOrderMonitoring(orderId: number): Observable<PaymentStatusUpdate> {
    console.log(`🔔 Iniciando monitoramento do pedido ${orderId}`);

    if (this.currentSellerPaymentRequestId !== null) {
      console.log(
        `⚠️ Encerrando monitoramento seller (request ${this.currentSellerPaymentRequestId}) → pedido ${orderId}`,
      );
      this.cleanupSubscriptions();
      this.webSocketService.unsubscribe(SELLER_PAYMENT_STATUS_CHANNEL, {
        seller_payment_request_id: this.currentSellerPaymentRequestId,
      });
      this.currentSellerPaymentRequestId = null;
    }

    // IMPORTANTE: Se já está monitorando este MESMO pedido, NÃO reiniciar
    // Mesmo que wsSubscription seja null (ainda conectando), não limpar
    if (this.currentOrderId === orderId) {
      console.log(
        `📡 Já monitorando (ou conectando para) o pedido ${orderId}, reutilizando`,
      );
      console.log(`   - wsSubscription existe? ${!!this.wsSubscription}`);
      console.log(
        `   - wsConnectionSubscription existe? ${!!this.wsConnectionSubscription}`,
      );
      return this.paymentStatusSubject$.asObservable();
    }

    // Se está monitorando outro pedido, aí sim limpar
    if (this.currentOrderId !== null && this.currentOrderId !== orderId) {
      console.log(
        `⚠️ Trocando monitoramento do pedido ${this.currentOrderId} para ${orderId}`,
      );
      this.cleanupSubscriptions();
    }

    this.currentOrderId = orderId;
    this.updateState({ orderId, loading: true, error: null });

    // Conectar ao WebSocket se não estiver conectado
    // IMPORTANTE: Gerenciar esta subscrição para evitar múltiplas chamadas
    console.log(`🔌 Criando subscrição ao WebSocket para order ${orderId}`);
    this.wsConnectionSubscription = this.webSocketService
      .connect()
      .subscribe((state) => {
        if (state.connected) {
          console.log(
            `✅ WebSocket conectado, subscrevendo ao pedido ${orderId}`,
          );
          this.subscribeToOrder(orderId);
        } else if (state.error) {
          console.error("❌ Erro ao conectar WebSocket:", state.error);
          this.updateState({ error: state.error });
        }
      });

    return this.paymentStatusSubject$.asObservable();
  }

  /**
   * Cobrança PIX/link do vendedor — mesmo contrato do Rails `SellerPaymentStatusChannel`
   * (`seller_payment_request_id` + stream `seller_payment_status_*`).
   */
  startSellerPaymentRequestMonitoring(
    sellerPaymentRequestId: number,
  ): Observable<PaymentStatusUpdate> {
    console.log(
      `🔔 Iniciando monitoramento SellerPaymentStatusChannel request ${sellerPaymentRequestId}`,
    );

    if (this.currentOrderId !== null) {
      console.log(
        `⚠️ Encerrando PaymentStatusChannel (pedido ${this.currentOrderId}) → seller request ${sellerPaymentRequestId}`,
      );
      this.cleanupSubscriptions();
      this.webSocketService.unsubscribe("PaymentStatusChannel", {
        order_id: this.currentOrderId,
      });
      this.currentOrderId = null;
    }

    if (this.currentSellerPaymentRequestId === sellerPaymentRequestId) {
      console.log(
        `📡 Já monitorando seller_payment_request ${sellerPaymentRequestId}, reutilizando`,
      );
      return this.paymentStatusSubject$.asObservable();
    }

    if (
      this.currentSellerPaymentRequestId !== null &&
      this.currentSellerPaymentRequestId !== sellerPaymentRequestId
    ) {
      this.cleanupSubscriptions();
      this.webSocketService.unsubscribe(SELLER_PAYMENT_STATUS_CHANNEL, {
        seller_payment_request_id: this.currentSellerPaymentRequestId,
      });
    }

    this.currentSellerPaymentRequestId = sellerPaymentRequestId;
    this.updateState({
      orderId: null,
      loading: true,
      error: null,
    });

    this.wsConnectionSubscription = this.webSocketService
      .connect()
      .subscribe((state) => {
        if (state.connected) {
          this.subscribeToSellerPaymentRequest(sellerPaymentRequestId);
        } else if (state.error) {
          console.error("❌ Erro ao conectar WebSocket:", state.error);
          this.updateState({ error: state.error });
        }
      });

    return this.paymentStatusSubject$.asObservable();
  }

  /**
   * Para o monitoramento do pedido atual
   */
  stopOrderMonitoring(): void {
    console.log("🛑 Parando monitoramento do pedido");
    this.cleanupAll();
    this.updateState({
      orderId: null,
      status: null,
      loading: false,
      error: null,
      data: null,
    });
  }

  /**
   * Obtém o estado atual do pagamento
   */
  getPaymentState(): Observable<PaymentStatusState> {
    return this.stateSubject$.asObservable();
  }

  /**
   * Obtém o estado atual do pagamento (snapshot)
   */
  getCurrentPaymentState(): PaymentStatusState {
    return { ...this.currentState };
  }

  /**
   * Verifica se está monitorando algum pedido
   */
  isMonitoring(): boolean {
    return (
      this.currentOrderId !== null ||
      this.currentSellerPaymentRequestId !== null
    );
  }

  /**
   * Obtém o ID do pedido sendo monitorado
   */
  getCurrentOrderId(): number | null {
    return this.currentOrderId;
  }

  /**
   * Obtém notificações filtradas por status específico
   */
  getNotificationsByStatus(status: string): Observable<PaymentStatusUpdate> {
    return this.paymentStatusSubject$.pipe(
      filter((update) => update.status === status),
    );
  }

  /**
   * Obtém notificações filtradas por tipo de evento
   */
  getNotificationsByType(type: string): Observable<PaymentStatusUpdate> {
    return this.paymentStatusSubject$.pipe(
      filter((update) => update.type === type),
    );
  }

  /**
   * Verifica se o pagamento foi bem-sucedido
   */
  isPaymentSuccessful(): boolean {
    return this.currentState.status === "succeeded";
  }

  /**
   * Verifica se o pagamento falhou
   */
  isPaymentFailed(): boolean {
    return this.currentState.status === "failed";
  }

  /**
   * Verifica se o pagamento está sendo processado
   */
  isPaymentProcessing(): boolean {
    return this.currentState.status === "processing";
  }

  /**
   * Verifica se o pagamento requer ação do usuário
   */
  requiresPaymentMethod(): boolean {
    return this.currentState.status === "requires_payment_method";
  }

  /**
   * Obtém a razão da falha (se houver)
   */
  getFailureReason(): string | null {
    return this.currentState.data?.failure_reason || null;
  }

  private subscribeToOrder(orderId: number): void {
    console.log(`📡 [subscribeToOrder] Chamado para order_id: ${orderId}`);
    console.log(`   - wsSubscription existe? ${!!this.wsSubscription}`);
    console.log(`   - currentOrderId: ${this.currentOrderId}`);

    // IMPORTANTE: Não desinscrever aqui! Isso causa race condition
    // Apenas subscrever se não estiver já subscrito
    if (this.wsSubscription && this.currentOrderId === orderId) {
      console.log("📡 Já monitorando este pedido:", orderId);
      return;
    }

    // Se estiver monitorando outro pedido, aí sim cancelar
    if (this.wsSubscription && this.currentOrderId !== orderId) {
      console.log(
        `⚠️ Trocando monitoramento de order ${this.currentOrderId} para ${orderId}`,
      );
      this.unsubscribeFromPaymentChannel();
    }

    console.log(
      `✅ Subscrevendo ao PaymentStatusChannel para order ${orderId}`,
    );
    // Subscrever ao canal PaymentStatusChannel
    this.wsSubscription = this.webSocketService
      .subscribe("PaymentStatusChannel", { order_id: orderId })
      .pipe(
        filter((message) =>
          message.identifier?.includes(`order_id\":${orderId}`),
        ),
        map((message) => message.message),
      )
      .subscribe({
        next: (update: PaymentStatusUpdate) => {
          console.log(
            `📥 Mensagem recebida do WebSocket para order ${orderId}:`,
            update,
          );
          this.handlePaymentStatusUpdate(update);
        },
        error: (error) => {
          console.error("❌ Erro na subscrição WebSocket:", error);
          this.updateState({ error: "Erro na conexão" });
        },
      });

    console.log(`✓ Subscrição criada com sucesso para order ${orderId}`);
  }

  private subscribeToSellerPaymentRequest(sellerPaymentRequestId: number): void {
    if (
      this.wsSubscription &&
      this.currentSellerPaymentRequestId === sellerPaymentRequestId
    ) {
      return;
    }

    if (
      this.wsSubscription &&
      this.currentSellerPaymentRequestId !== sellerPaymentRequestId
    ) {
      this.unsubscribeFromPaymentChannel();
    }

    console.log(
      `✅ Subscrevendo ${SELLER_PAYMENT_STATUS_CHANNEL} seller_payment_request_id=${sellerPaymentRequestId}`,
    );

    this.wsSubscription = this.webSocketService
      .subscribe(SELLER_PAYMENT_STATUS_CHANNEL, {
        seller_payment_request_id: sellerPaymentRequestId,
      })
      .pipe(
        filter((message) =>
          message.identifier?.includes(
            `"seller_payment_request_id":${sellerPaymentRequestId}`,
          ),
        ),
        map((message) => this.normalizeSellerPaymentWsPayload(message.message)),
      )
      .subscribe({
        next: (update: PaymentStatusUpdate) => {
          console.log(
            `📥 WebSocket seller_payment_request ${sellerPaymentRequestId}:`,
            update,
          );
          this.handlePaymentStatusUpdate(update);
        },
        error: (error) => {
          console.error("❌ Erro na subscrição WebSocket (seller):", error);
          this.updateState({ error: "Erro na conexão" });
        },
      });
  }

  /** Payload Rails: type, seller_payment_request_id, status, data (data inclui order_id quando pago). */
  private normalizeSellerPaymentWsPayload(
    raw: unknown,
  ): PaymentStatusUpdate {
    if (!raw || typeof raw !== "object") {
      return {
        type: "seller_payment_status_update",
        order_id: 0,
        status: "",
        data: {
          payment_method: "",
          amount: 0,
          timestamp: "",
          correlation_id: "",
        },
      };
    }
    const r = raw as Record<string, unknown>;
    const data = (r["data"] as PaymentStatusUpdate["data"]) ?? {
      payment_method: "",
      amount: 0,
      timestamp: "",
      correlation_id: "",
    };
    const orderIdTop = r["order_id"];
    const orderId =
      typeof orderIdTop === "number"
        ? orderIdTop
        : typeof data.order_id === "number"
          ? data.order_id
          : 0;

    return {
      type: String(r["type"] ?? "seller_payment_status_update"),
      order_id: orderId,
      status: String(r["status"] ?? ""),
      data,
    };
  }

  private unsubscribeFromPaymentChannel(): void {
    console.log(
      "📴 [unsubscribeFromPaymentChannel] Desinscrevendo do canal de pagamento",
    );
    console.log(`   - Order ID atual: ${this.currentOrderId}`);
    console.log(
      `   - Seller payment_request ID atual: ${this.currentSellerPaymentRequestId}`,
    );
    console.log(`   - Stack trace:`, new Error().stack);

    if (this.wsSubscription) {
      console.log("   ✓ Cancelando wsSubscription (Observable)");
      this.wsSubscription.unsubscribe();
      this.wsSubscription = null;
    }

    if (this.currentOrderId !== null) {
      console.log(
        `   ✓ Enviando unsubscribe ao ActionCable para order ${this.currentOrderId}`,
      );
      this.webSocketService.unsubscribe("PaymentStatusChannel", {
        order_id: this.currentOrderId,
      });
    }

    if (this.currentSellerPaymentRequestId !== null) {
      this.webSocketService.unsubscribe(SELLER_PAYMENT_STATUS_CHANNEL, {
        seller_payment_request_id: this.currentSellerPaymentRequestId,
      });
    }

    console.log("   ✓ Unsubscribe completo");
  }

  /**
   * Limpa apenas as subscrições, sem resetar o estado
   */
  private cleanupSubscriptions(): void {
    // Cancelar timeout de unsubscribe pendente
    if (this.unsubscribeTimeout) {
      clearTimeout(this.unsubscribeTimeout);
      this.unsubscribeTimeout = null;
    }

    // Limpar subscrição da conexão WebSocket
    if (this.wsConnectionSubscription) {
      this.wsConnectionSubscription.unsubscribe();
      this.wsConnectionSubscription = null;
    }

    // Limpar subscrição do canal
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = null;
    }
  }

  /**
   * Limpa tudo: subscrições, timeouts e desinscreve do canal
   */
  private cleanupAll(): void {
    this.cleanupSubscriptions();

    // Desinscrever do canal ActionCable
    if (this.currentOrderId !== null) {
      this.webSocketService.unsubscribe("PaymentStatusChannel", {
        order_id: this.currentOrderId,
      });
      this.currentOrderId = null;
    }
    if (this.currentSellerPaymentRequestId !== null) {
      this.webSocketService.unsubscribe(SELLER_PAYMENT_STATUS_CHANNEL, {
        seller_payment_request_id: this.currentSellerPaymentRequestId,
      });
      this.currentSellerPaymentRequestId = null;
    }
  }

  private handlePaymentStatusUpdate(update: PaymentStatusUpdate): void {
    console.log("✅ Atualização de status recebida via WebSocket:", update);

    const orderIdResolved =
      update.order_id ||
      update.data?.order_id ||
      0;
    const forEmit: PaymentStatusUpdate = {
      ...update,
      order_id: orderIdResolved,
    };

    // Emitir a notificação
    this.paymentStatusSubject$.next(forEmit);

    // Atualizar o estado
    const resolvedOrderId =
      forEmit.order_id || this.currentState.orderId;

    this.updateState({
      orderId: resolvedOrderId || null,
      status: forEmit.status,
      data: forEmit.data,
      loading: false,
      error: null,
    });

    // Se o pagamento foi finalizado (sucesso ou falha), parar o monitoramento
    // AGUARDAR 20 segundos para garantir que não há mais atualizações pendentes
    const st = forEmit.status?.toLowerCase() ?? "";
    if (
      st === "succeeded" ||
      st === "paid" ||
      st === "failed" ||
      st === "canceled" ||
      st === "cancelled" ||
      st === "expired"
    ) {
      console.log(
        `🕒 Pagamento finalizado (${st}). Mantendo subscrição por mais 20s para garantir...`,
      );

      // Cancelar timeout anterior se existir
      if (this.unsubscribeTimeout) {
        clearTimeout(this.unsubscribeTimeout);
      }

      // Agendar novo timeout
      this.unsubscribeTimeout = setTimeout(() => {
        console.log("📴 Desconectando do WebSocket após período de espera");
        this.unsubscribeFromPaymentChannel();
        this.unsubscribeTimeout = null;
      }, 20000); // Aguardar 20 segundos antes de parar (aumentado de 15s)
    }
  }

  private updateState(partialState: Partial<PaymentStatusState>): void {
    this.currentState = { ...this.currentState, ...partialState };
    this.stateSubject$.next(this.currentState);
  }
}
